import React from 'react';

import { DatePicker } from '@opendoor/bricks/complex/DatePicker';

import { ErrorMessage } from 'sellDirect/components/closingDash/ErrorMessage';

import { OD_CONTACT_EMAIL } from 'sellDirect/helpers/closingDash/globals';
import { convert, LocalDate } from '@js-joda/core';
import { localDateFromJsDate } from 'shared/helpers/datetime';
import { useExperimentationContext } from 'sellDirect/ExperimentationContext';
import { CXA_EMAIL_NEW } from 'shared/helpers/globals';

interface CloseDatePickerProps {
  /** The list of available close dates for the user to choose from */
  availableCloseDates: LocalDate[];
  /** The current close date (if any) */
  closeDate?: LocalDate;
  /** Callback to set the new close date */
  onUpdate: (value: LocalDate) => void;
  /** Disables the date picker */
  disabled: boolean;
  /** Whether to display the agent-only or core label */
  shouldSeeAgentExperience: boolean;
}

/**
 * Close date display and picker.
 *
 * component-test-check-ignore
 */
export const CloseDatePicker: React.FC<CloseDatePickerProps> = ({
  availableCloseDates,
  onUpdate,
  closeDate,
  disabled,
  shouldSeeAgentExperience,
}) => {
  const {
    assertions: { isCepEmailUpdatesEnabled },
  } = useExperimentationContext();

  return (
    <>
      {!availableCloseDates && (
        <ErrorMessage
          message={`We ran into an issue finding available dates. Email ${
            isCepEmailUpdatesEnabled ? CXA_EMAIL_NEW : OD_CONTACT_EMAIL
          } to get help.`}
        />
      )}
      <DatePicker
        id="change-close-of-escrow-date-picker"
        label={`Choose ${shouldSeeAgentExperience ? 'your client’s' : 'your'} close date`}
        placeholder="Month, Day, Year"
        disabled={disabled}
        initialDate={closeDate ? convert(closeDate).toDate() : undefined}
        isOutOfRange={(date: Date) =>
          !availableCloseDates.some((availableDate) =>
            localDateFromJsDate(date).equals(availableDate),
          )
        }
        onDateChange={(date: Date) => onUpdate(localDateFromJsDate(date))}
      />
    </>
  );
};
